import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, RefreshCw, Droplets, ArrowRightLeft, ArchiveX,
  Landmark, Leaf, ArrowDownRight, ArrowUpRight, TrendingDown,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

const CREDIT_TYPES = ['VCU', 'CER', 'GS_VER', 'I-REC', 'Custom'];
const REGISTRIES = ['Verra', 'Gold Standard', 'CDM', 'I-REC', 'Custom'];

export default function CreditsPanel({ siteId }) {
  const { showSuccess, showError } = useAlert();
  const [credits, setCredits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vintage, setVintage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [actionState, setActionState] = useState(null); // {type:'retire'|'transfer', credit}

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const qs = siteId ? `?site_id=${siteId}` : '';
      const [c, t, s, v] = await Promise.all([
        axios.get(`${API}/mrv/carbon/credits`),
        axios.get(`${API}/mrv/carbon/transactions`),
        axios.get(`${API}/mrv/carbon/summary${qs}`),
        axios.get(`${API}/mrv/carbon/by-vintage`),
      ]);
      setCredits(c.data.credits || []);
      setTransactions(t.data.transactions || []);
      setSummary(s.data);
      setVintage(v.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, [showError, siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteCredit = async (id) => {
    if (!window.confirm('Delete this credit? Only allowed if no retirement/transfer exists.')) return;
    try {
      await axios.delete(`${API}/mrv/carbon/credits/${id}`);
      showSuccess('Credit deleted');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Carbon Asset Ledger</h2>
          <div className="text-xs text-gray-500">M6 — Track carbon credits, retirements, and net carbon position</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(s => !s)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Issue credit
          </button>
          <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* 5 KPI cards (matches saarv-mrv-beta layout) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <BigStat
          label="Total Issued"
          value={Number(summary?.totals?.total_issued || 0).toFixed(0) + ' t'}
          sub={`${summary?.totals?.total_count || 0} credit batch(es)`}
          Icon={Leaf}
          accent="emerald"
        />
        <BigStat
          label="Available"
          value={Number(summary?.totals?.total_available || 0).toFixed(0) + ' t'}
          sub="Currently held"
          Icon={Landmark}
          accent="blue"
        />
        <BigStat
          label="Retired"
          value={Number(summary?.totals?.total_retired || 0).toFixed(0) + ' t'}
          sub="Permanently retired"
          Icon={ArrowDownRight}
          accent="violet"
        />
        <BigStat
          label="Transferred"
          value={Number(summary?.totals?.total_transferred || 0).toFixed(0) + ' t'}
          sub="Transferred out"
          Icon={ArrowUpRight}
          accent="amber"
        />
        <BigStat
          label="Net Position"
          value={vintage?.net_position_tco2e != null
            ? `${vintage.net_position_tco2e > 0 ? '' : ''}${Number(vintage.net_position_tco2e).toFixed(2)} t`
            : '—'}
          sub={`Retired (${Number(vintage?.retired_total_tco2e || 0).toFixed(0)}t) − Emissions (${Number(vintage?.emissions_12m_tco2e || 0).toFixed(2)}t)`}
          Icon={vintage?.net_position_tco2e >= 0 ? Leaf : TrendingDown}
          accent={vintage?.net_position_tco2e >= 0 ? 'emerald' : 'red'}
        />
      </div>

      {/* Charts — Credits by Vintage + By Credit Type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Credits by Vintage</h3>
            <span className="text-xs text-gray-500">Issued vs available by vintage year</span>
          </div>
          {(vintage?.by_vintage || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={vintage.by_vintage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="vintage_year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => `${Number(v).toFixed(0)} tCO2e`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="issued"    name="Issued"    fill="#16a34a" />
                <Bar dataKey="available" name="Available" fill="#2563eb" />
                <Bar dataKey="retired"   name="Retired"   fill="#7c3aed" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-gray-400 text-center py-12">No credits issued yet</div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">By Credit Type</h3>
          {(summary?.by_credit_type || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={summary.by_credit_type}
                  dataKey="total"
                  nameKey="credit_type"
                  innerRadius={50}
                  outerRadius={80}
                  label={({ credit_type }) => credit_type}
                >
                  {summary.by_credit_type.map((_, i) => (
                    <Cell key={i} fill={['#16a34a', '#2563eb', '#7c3aed', '#f59e0b', '#dc2626'][i % 5]} />
                  ))}
                </Pie>
                <Tooltip formatter={v => `${Number(v).toFixed(0)} tCO2e`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-xs text-gray-400 text-center py-12">No credits</div>
          )}
        </div>
      </div>

      {showCreate && <CreateCreditForm onCreated={() => { setShowCreate(false); fetchAll(); }} onCancel={() => setShowCreate(false)} />}

      {actionState && (
        <RetireTransferForm
          state={actionState}
          onClose={() => setActionState(null)}
          onDone={() => { setActionState(null); fetchAll(); }}
        />
      )}

      {/* Credits table */}
      <div className="overflow-x-auto border border-gray-200 rounded-md mb-6">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Registry</th>
              <th className="px-3 py-2 font-semibold text-gray-700">External Ref</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Vintage</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">Issued</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">Available</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {credits.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">No credits issued yet</td></tr>
            )}
            {credits.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{c.credit_type}</td>
                <td className="px-3 py-2 text-gray-600">{c.registry || '—'}</td>
                <td className="px-3 py-2 text-gray-600 font-mono text-xs">{c.external_ref || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{c.vintage_year || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(c.quantity_tco2e).toFixed(0)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{Number(c.quantity_available).toFixed(0)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    c.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'retired' ? 'bg-violet-100 text-violet-700' :
                    c.status === 'transferred' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{c.status}</span>
                </td>
                <td className="px-3 py-2 flex gap-1 justify-end">
                  {c.status === 'active' && (
                    <>
                      <button onClick={() => setActionState({ type: 'retire', credit: c })}
                              className="text-violet-600 hover:text-violet-800 p-1" title="Retire">
                        <ArchiveX className="w-4 h-4" />
                      </button>
                      <button onClick={() => setActionState({ type: 'transfer', credit: c })}
                              className="text-blue-600 hover:text-blue-800 p-1" title="Transfer">
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button onClick={() => deleteCredit(c.id)} className="text-red-500 hover:text-red-700 p-1" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent transactions */}
      <h3 className="text-base font-semibold text-gray-900 mb-2">Recent ledger transactions</h3>
      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Date</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700 text-right">Quantity</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Counterparty</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Hash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {transactions.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">No transactions</td></tr>
            )}
            {transactions.slice(0, 30).map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs">{t.tx_date ? new Date(t.tx_date).toLocaleString() : ''}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    t.tx_type === 'issuance' ? 'bg-emerald-100 text-emerald-700' :
                    t.tx_type === 'retirement' ? 'bg-violet-100 text-violet-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>{t.tx_type}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono">{Number(t.quantity).toFixed(0)}</td>
                <td className="px-3 py-2 text-gray-600 text-xs">{t.counterparty || '—'}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-gray-400 truncate max-w-[160px]" title={t.tx_hash}>
                  {t.tx_hash ? t.tx_hash.slice(0, 16) + '…' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'gray' }) {
  const map = {
    gray:    'bg-gray-50 text-gray-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    violet:  'bg-violet-50 text-violet-700',
    blue:    'bg-blue-50 text-blue-700',
  };
  return (
    <div className={`p-3 rounded border border-gray-200 ${map[accent]}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function BigStat({ label, value, sub, Icon, accent = 'gray' }) {
  const map = {
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700',    iconBg: 'bg-gray-100',    iconText: 'text-gray-600' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100',    iconText: 'text-blue-600' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  iconBg: 'bg-violet-100',  iconText: 'text-violet-600' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   iconBg: 'bg-amber-100',   iconText: 'text-amber-600' },
    red:     { bg: 'bg-red-50',     text: 'text-red-700',     iconBg: 'bg-red-100',     iconText: 'text-red-600' },
  };
  const a = map[accent] || map.gray;
  return (
    <div className={`p-4 rounded-lg border border-gray-200 ${a.bg} relative`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`text-xs font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-md ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${a.iconText}`} />
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
  );
}

function CreateCreditForm({ onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({
    credit_type: 'VCU', registry: 'Verra', external_ref: '',
    vintage_year: new Date().getFullYear(), quantity_tco2e: '',
    unit_price: '', currency: 'USD',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.quantity_tco2e || form.quantity_tco2e <= 0) {
      showError('Quantity must be positive');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/carbon/credits`, {
        credit_type: form.credit_type,
        registry: form.registry,
        external_ref: form.external_ref || null,
        vintage_year: form.vintage_year ? parseInt(form.vintage_year) : null,
        quantity_tco2e: parseFloat(form.quantity_tco2e),
        unit_price: form.unit_price ? parseFloat(form.unit_price) : null,
        currency: form.currency,
      });
      showSuccess('Credit issued');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Issue failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        <select value={form.credit_type}
                onChange={e => setForm(f => ({ ...f, credit_type: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {CREDIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={form.registry}
                onChange={e => setForm(f => ({ ...f, registry: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {REGISTRIES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input placeholder="External ref" value={form.external_ref}
               onChange={e => setForm(f => ({ ...f, external_ref: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" placeholder="Vintage year" value={form.vintage_year}
               onChange={e => setForm(f => ({ ...f, vintage_year: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" step="any" placeholder="Quantity (tCO2e)"
               value={form.quantity_tco2e}
               onChange={e => setForm(f => ({ ...f, quantity_tco2e: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" step="any" placeholder="Unit price"
               value={form.unit_price}
               onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Currency" value={form.currency}
               onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Issuing…' : 'Issue'}
        </button>
      </div>
    </div>
  );
}

function RetireTransferForm({ state, onClose, onDone }) {
  const { showError, showSuccess } = useAlert();
  const [qty, setQty] = useState('');
  const [counterparty, setCp] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const isTransfer = state.type === 'transfer';

  const submit = async () => {
    if (!qty || qty <= 0) { showError('Quantity must be positive'); return; }
    if (isTransfer && !counterparty) { showError('Counterparty required for transfer'); return; }
    setSaving(true);
    try {
      const url = isTransfer
        ? `${API}/mrv/carbon/credits/${state.credit.id}/transfer`
        : `${API}/mrv/carbon/credits/${state.credit.id}/retire`;
      const body = { quantity: parseFloat(qty), notes: notes || null };
      if (isTransfer) body.counterparty = counterparty;
      else if (counterparty) body.counterparty = counterparty;
      await axios.post(url, body);
      showSuccess(`${isTransfer ? 'Transferred' : 'Retired'} ${qty} tCO2e`);
      onDone();
    } catch (e) {
      showError(e.response?.data?.detail || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border-2 border-violet-300 bg-violet-50 rounded-md">
      <div className="font-semibold text-violet-900 mb-2">
        {isTransfer ? 'Transfer credit' : 'Retire credit'} — {state.credit.credit_type} (avail: {state.credit.quantity_available})
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input type="number" step="any" placeholder="Quantity (tCO2e)"
               value={qty} onChange={e => setQty(e.target.value)}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder={isTransfer ? 'Counterparty (required)' : 'Counterparty (optional)'}
               value={counterparty} onChange={e => setCp(e.target.value)}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Notes" value={notes} onChange={e => setNotes(e.target.value)}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onClose} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-violet-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Working…' : (isTransfer ? 'Transfer' : 'Retire')}
        </button>
      </div>
    </div>
  );
}
